import api from './api';
import { getRememberedUsers } from './offlineUsers';

/**
 * Which Canvas children the signed-in user has data for (task 6.0f).
 *
 * ## Why this exists
 *
 * `GetSnapshots` queries `PK = USER#{cognito}_CANVAS_USER#{canvasUser}`, so it
 * needs the Canvas user id up front, and DynamoDB cannot prefix-match a
 * partition key. Task 6.0e answered "which children?" from
 * `offlineUsers.js` — a localStorage record written at import time, correct
 * only on the browser that performed the import. Sign in on a second device and
 * the selector was empty even though the data was there.
 *
 * The server now maintains an index row per child, so this asks it.
 */

/**
 * Every Canvas child the server has data for, most recent capture first.
 *
 * Falls back to the local record when the request fails, rather than throwing:
 * the pre-6.0f behaviour is strictly better than an empty selector, and this
 * screen exists to be usable when things are broken. The two sources are
 * merged rather than one replacing the other, because a browser that has just
 * imported may know about a child whose index row failed to write — see
 * `record_child`'s callers, which never fail an import over the index.
 */
export const getCanvasUsers = async () => {
  const local = getRememberedUsers().map((entry) => ({
    canvasUserId: String(entry.id),
    label: entry.label || null,
    rootUrl: entry.rootUrl || null,
    lastSnapshotAt: entry.lastImportedAt || null,
    source: 'local'
  }));

  let remote = [];
  try {
    const response = await api.get('/canvas-users');
    remote = Array.isArray(response?.users) ? response.users : [];
  } catch (error) {
    // Not surfaced: an empty or stale selector is a degraded experience, not a
    // failure worth blocking on, and manual entry always remains available.
    console.warn('Could not list Canvas users from the server:', error);
    return local;
  }

  // Server first, so its label and date win for any child both know about.
  const seen = new Set(remote.map((user) => String(user.canvasUserId)));
  return [...remote, ...local.filter((user) => !seen.has(user.canvasUserId))];
};

export default { getCanvasUsers };
